import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ShoppingCart, ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { getProductBySlug, getRelatedProducts } from "@/data/products";
import { useCart } from "@/hooks/use-cart";

export const Route = createFileRoute("/product/$slug")({
  head: ({ params }) => {
    const product = getProductBySlug(params.slug);
    const url = `https://shopb.life/product/${params.slug}`;
    const clamp = (s: string, max: number) => {
      if (s.length <= max) return s;
      const cut = s.slice(0, max - 1);
      const lastSpace = cut.lastIndexOf(" ");
      return (lastSpace > max - 20 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
    };
    const suffix = " — angle-shop.com";
    const rawTitle = product ? `${product.name}${suffix}` : `Produit${suffix}`;
    const title = rawTitle.length <= 60 ? rawTitle : clamp(product?.name ?? "Produit", 60 - suffix.length) + suffix;
    const description = clamp(product?.description ?? "", 160);
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "product" },
    ];
    if (product?.image) {
      const absImage = product.image.startsWith("http")
        ? product.image
        : `https://shopb.life${product.image.startsWith("/") ? "" : "/"}${product.image}`;
      meta.push({ property: "og:image", content: absImage });
      meta.push({ name: "twitter:image", content: absImage });
      meta.push({ name: "twitter:card", content: "summary_large_image" });
    }
    const absImageForLd = product?.image
      ? product.image.startsWith("http")
        ? product.image
        : `https://shopb.life${product.image.startsWith("/") ? "" : "/"}${product.image}`
      : undefined;
    const brand = product?.name?.split(" ")[0] ?? "angle-shop";
    const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    const scripts = product
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: product.description,
              image: absImageForLd,
              sku: product.id,
              mpn: product.id,
              brand: { "@type": "Brand", name: brand },
              category: product.category,
              offers: {
                "@type": "Offer",
                url,
                priceCurrency: "EUR",
                price: product.price,
                priceValidUntil,
                itemCondition: "https://schema.org/NewCondition",
                availability: product.inStock
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
                seller: { "@type": "Organization", name: "angle-shop.com" },
              },
            }),
          },
        ]
      : [];
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  loader: ({ params }) => {
    const product = getProductBySlug(params.slug);
    if (!product) throw notFound();
    return { product };
  },
  component: ProductPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <Link to="/catalog" className="text-electric mt-4 inline-block">Back to catalog</Link>
      </div>
    </div>
  ),
});

function ProductPage() {
  const loaderData = Route.useLoaderData() as { product: import("@/data/products").Product };
  const product = loaderData.product;
  const { addItem } = useCart();
  const related = getRelatedProducts(product.id, 3);
  const allImages = product.images && product.images.length > 0 ? product.images : [product.image];
  const totalMedia = allImages.length + (product.video ? 1 : 0);
  const [selectedMedia, setSelectedMedia] = useState(0);
  const isVideoSelected = product.video && selectedMedia === allImages.length;
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    product.variants?.forEach((v) => { defaults[v.name] = v.options[0]; });
    return defaults;
  });

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/catalog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Retour au catalogue
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            <div className="aspect-square bg-card rounded-3xl overflow-hidden shadow-card">
              {isVideoSelected ? (
                <video src={product.video} controls autoPlay muted className="w-full h-full object-cover" />
              ) : (
                <img src={allImages[selectedMedia]} alt={product.name} width={800} height={800} className="w-full h-full object-cover" />
              )}
            </div>
            {totalMedia > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-3">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedMedia(i)}
                    aria-label={`Voir l'image ${i + 1}`}
                    className={`aspect-square w-full rounded-xl overflow-hidden border-2 transition-all ${
                      selectedMedia === i && !isVideoSelected ? "border-electric shadow-electric" : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} loading="lazy" className="w-full h-full object-cover object-center" />
                  </button>
                ))}
                {product.video && (
                  <button
                    onClick={() => setSelectedMedia(allImages.length)}
                    aria-label="Voir la vidéo"
                    className={`aspect-square w-full rounded-xl overflow-hidden border-2 transition-all relative ${
                      isVideoSelected ? "border-electric shadow-electric" : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <video src={product.video} muted playsInline className="w-full h-full object-cover object-center" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                        <div className="w-0 h-0 border-l-[8px] border-l-foreground border-y-[5px] border-y-transparent ml-0.5" />
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {product.badge && (
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
                product.badge === "bestseller" ? "bg-badge-success text-electric-foreground" :
                product.badge === "new" ? "bg-badge-new text-electric-foreground" :
                "bg-electric text-electric-foreground"
              }`}>
                {product.badge === "bestseller" ? "Bestseller" : product.badge === "new" ? "New" : "Bundle"}
              </span>
            )}
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">{product.name}</h1>
            <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-electric/10 border border-electric/20">
              <span className="text-xs font-bold text-electric animate-pulse">✨</span>
              <span className="text-sm font-semibold text-electric">Exclusivement aujourd'hui — -10%</span>
            </div>
            <div className="flex items-baseline gap-3 mt-3">
              <span className="text-3xl font-bold text-foreground">{product.price.toFixed(2)}€</span>
              {product.originalPrice && (
                <span className="text-lg text-muted-foreground line-through">{product.originalPrice.toFixed(2)}€</span>
              )}
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

            <ul className="mt-8 space-y-3">
              {product.features.map((feature: string) => (
                <li key={feature} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-badge-success/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-badge-success" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            {product.compatibleWith.length > 0 && (
              <div className="mt-8 p-4 rounded-xl bg-surface border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-2">Compatible with</h3>
                <ul className="space-y-1">
                  {product.compatibleWith.map((c: string) => (
                    <li key={c} className="text-sm text-muted-foreground">• {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {product.variants && product.variants.length > 0 && (
              <div className="mt-8 space-y-4">
                {product.variants.map((variant) => (
                  <div key={variant.name}>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{variant.name}</h3>
                    <div className="flex gap-3">
                      {variant.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => setSelectedVariants((prev) => ({ ...prev, [variant.name]: option }))}
                          className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                            selectedVariants[variant.name] === option
                              ? "border-electric bg-electric/10 text-electric"
                              : "border-border text-muted-foreground hover:border-muted-foreground"
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => addItem(product)}
              className="mt-8 w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-electric text-electric-foreground font-semibold text-base shadow-electric hover:opacity-90 transition-opacity"
            >
              <ShoppingCart className="w-5 h-5" />
              Add to cart
            </button>
          </motion.div>
        </div>

        {related.length > 0 && (
          <section className="mt-20 sm:mt-28">
            <h2 className="font-display text-2xl font-bold text-foreground tracking-tight mb-8">You may also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </main>
  );
}
